import { useCallback, type Dispatch, type SetStateAction } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  DraftPair,
  DraftPlayer,
} from "../../challenges/types/challengeDraft";
import {
  buildChallengeRoundsFromPairs,
  buildDrawPairs,
  createPairId,
  normalizeCpf,
} from "../../challenges/utils/challengePairs";
import { buildSuper8Schedule } from "../../challenges/utils/super8Schedule";
import { buildPocoRoundsFromPairs } from "../../challenges/utils/pocoSchedule";

type UseAdminChallengePairActionsInput = {
  activeRegistrationCategoryKeyForModal?: string;
  draftPairs: DraftPair[];
  draftPlayers: DraftPlayer[];
  drawCpf: string;
  eligibleCpfSet: Set<string>;
  ensurePlayerProfile: (cpf: string) => Promise<void>;
  explainIneligibleUserByCpf: (cpf: string) => string;
  challengeCourtsCount?: number | null;
  challengeRoundsJson: string;
  isPoco: boolean;
  isSuper8: boolean;
  maxDrawPlayersForCurrentChallenge: number | null;
  maxFixedPairsForCurrentChallenge: number | null;
  nameByCpf: Record<string, string>;
  pairCpf1: string;
  pairCpf2: string;
  persistPairIfNeeded: (cpf1: string, cpf2: string) => Promise<void>;
  playerAlreadySelectedMessage: string;
  resolveChallengePlayerCpfInput: (value: string) => string;
  selectedCpfsSet: Set<string>;
  setChallengeRoundsJson: Dispatch<SetStateAction<string>>;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setDraftPlayers: Dispatch<SetStateAction<DraftPlayer[]>>;
  setDrawCpf: Dispatch<SetStateAction<string>>;
  setPairCpf1: Dispatch<SetStateAction<string>>;
  setPairCpf2: Dispatch<SetStateAction<string>>;
  setParticipantsFromPairs: (nextPairs: DraftPair[]) => void;
  setParticipantsFromPlayers: (nextPlayers: DraftPlayer[]) => void;
  showErrorToast: (message: string) => void;
  showSuccessToast?: (message: string) => void;
  super8PlayersForCurrentChallenge: DraftPlayer[];
};

function parseRoundsJson(value: string) {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed.filter((round) => Array.isArray(round)) : [];
  } catch (_error) {
    return [];
  }
}

function tagRoundsWithCategory(rounds: any[], categoryKey: string) {
  const safeKey = String(categoryKey || "").trim();
  if (!safeKey) return rounds;
  return rounds.map((round: any[]) =>
    round.map((match: any) => ({ ...match, categoryKey: safeKey })),
  );
}

function replaceRoundsForCategory(currentJson: string, categoryKey: string, nextRounds: any[]) {
  const safeKey = String(categoryKey || "").trim();
  if (!safeKey) return JSON.stringify(nextRounds, null, 2);
  const currentRounds = parseRoundsJson(currentJson);
  const remainingRounds = currentRounds.filter((round: any[]) =>
    !round.some((match: any) => String(match?.categoryKey || "").trim() === safeKey),
  );
  return JSON.stringify([...remainingRounds, ...tagRoundsWithCategory(nextRounds, safeKey)], null, 2);
}

export function useAdminChallengePairActions({
  activeRegistrationCategoryKeyForModal = "",
  draftPairs,
  draftPlayers,
  drawCpf,
  eligibleCpfSet,
  ensurePlayerProfile,
  explainIneligibleUserByCpf,
  challengeCourtsCount,
  challengeRoundsJson,
  isPoco,
  isSuper8,
  maxDrawPlayersForCurrentChallenge,
  maxFixedPairsForCurrentChallenge,
  nameByCpf,
  pairCpf1,
  pairCpf2,
  persistPairIfNeeded,
  playerAlreadySelectedMessage,
  resolveChallengePlayerCpfInput,
  selectedCpfsSet,
  setChallengeRoundsJson,
  setDraftPairs,
  setDraftPlayers,
  setDrawCpf,
  setPairCpf1,
  setPairCpf2,
  setParticipantsFromPairs,
  setParticipantsFromPlayers,
  showErrorToast,
  showSuccessToast,
  super8PlayersForCurrentChallenge,
}: UseAdminChallengePairActionsInput) {
  const addDrawPlayer = useCallback(
    (rawValue?: string) => {
      const cpf = normalizeCpf(rawValue ?? drawCpf);
      if (!cpf || cpf.length < 8) return false;
      if (selectedCpfsSet.has(cpf)) return false;
      if (!eligibleCpfSet.has(cpf)) {
        showErrorToast(explainIneligibleUserByCpf(cpf));
        return false;
      }
      const alreadyInDraft = draftPlayers.some((p) => p.cpf === cpf);
      if (
        !alreadyInDraft &&
        maxDrawPlayersForCurrentChallenge !== null &&
        draftPlayers.length >= maxDrawPlayersForCurrentChallenge
      ) {
        showErrorToast(
          `Este desafio permite no máximo ${maxDrawPlayersForCurrentChallenge} jogadores para sorteio de duplas.`,
        );
        return false;
      }

      const side = { direita: true, esquerda: true };

      const next = (() => {
        const updated: DraftPlayer = {
          cpf,
          name: nameByCpf[cpf] || siteTexts.challengeEngine.labels.player,
          side,
        };
        return alreadyInDraft
          ? draftPlayers.map((p) => (p.cpf === cpf ? updated : p))
          : [...draftPlayers, updated];
      })();

      setDraftPlayers(next);
      setParticipantsFromPlayers(next);
      setDraftPairs([]);
      void ensurePlayerProfile(cpf);
      setDrawCpf("");
      showSuccessToast?.(
        `${nameByCpf[cpf] || siteTexts.challengeEngine.labels.player} adicionado aos participantes.`,
      );
      return true;
    },
    [
      draftPlayers,
      drawCpf,
      eligibleCpfSet,
      ensurePlayerProfile,
      explainIneligibleUserByCpf,
      maxDrawPlayersForCurrentChallenge,
      nameByCpf,
      selectedCpfsSet,
      setDraftPairs,
      setDraftPlayers,
      setDrawCpf,
      setParticipantsFromPlayers,
      showErrorToast,
      showSuccessToast,
    ],
  );

  const addFixedPair = useCallback(() => {
    if (
      maxFixedPairsForCurrentChallenge !== null &&
      draftPairs.length >= maxFixedPairsForCurrentChallenge
    ) {
      showErrorToast(
        `Este desafio permite no máximo ${maxFixedPairsForCurrentChallenge} duplas (${maxFixedPairsForCurrentChallenge * 2} participantes).`,
      );
      return;
    }
    const cpf1 = resolveChallengePlayerCpfInput(pairCpf1);
    const cpf2 = resolveChallengePlayerCpfInput(pairCpf2);
    if (!cpf1 || !cpf2) {
      showErrorToast("Selecione os dois jogadores da dupla.");
      return;
    }
    if (cpf1 === cpf2) {
      showErrorToast("A dupla precisa ter dois jogadores diferentes.");
      return;
    }
    if (cpf1.length < 8 || cpf2.length < 8) {
      showErrorToast("Selecione jogadores com CPF válido.");
      return;
    }
    if (!eligibleCpfSet.has(cpf1) || !eligibleCpfSet.has(cpf2)) {
      const invalidCpf = !eligibleCpfSet.has(cpf1) ? cpf1 : cpf2;
      showErrorToast(explainIneligibleUserByCpf(invalidCpf));
      return;
    }
    const alreadySelected =
      selectedCpfsSet.has(cpf1) || selectedCpfsSet.has(cpf2);
    if (alreadySelected) {
      window.alert(playerAlreadySelectedMessage);
      return;
    }

    const id = createPairId(cpf1, cpf2);
    const label = `${nameByCpf[cpf1] || siteTexts.challengeEngine.labels.player} / ${
      nameByCpf[cpf2] || siteTexts.challengeEngine.labels.player
    }`;
    const next = draftPairs.some((pair) => pair.id === id)
      ? draftPairs
      : [...draftPairs, { id, cpf1, cpf2, label }];
    setDraftPairs(next);
    setParticipantsFromPairs(next);
    void persistPairIfNeeded(cpf1, cpf2);
    setPairCpf1("");
    setPairCpf2("");
    showSuccessToast?.("Dupla adicionada aos participantes.");
  }, [
    draftPairs,
    eligibleCpfSet,
    explainIneligibleUserByCpf,
    maxFixedPairsForCurrentChallenge,
    nameByCpf,
    pairCpf1,
    pairCpf2,
    persistPairIfNeeded,
    playerAlreadySelectedMessage,
    resolveChallengePlayerCpfInput,
    selectedCpfsSet,
    setDraftPairs,
    setPairCpf1,
    setPairCpf2,
    setParticipantsFromPairs,
    showErrorToast,
    showSuccessToast,
  ]);

  const generateFixedPairRounds = useCallback(() => {
    const rounds = isPoco
      ? buildPocoRoundsFromPairs(draftPairs, challengeCourtsCount)
      : buildChallengeRoundsFromPairs(draftPairs, challengeCourtsCount || 2);
    const categoryKey = String(activeRegistrationCategoryKeyForModal || "").trim();
    const nextJson = categoryKey
      ? replaceRoundsForCategory(challengeRoundsJson, categoryKey, rounds)
      : JSON.stringify(rounds, null, 2);
    setChallengeRoundsJson(nextJson);
    return parseRoundsJson(nextJson);
  }, [
    activeRegistrationCategoryKeyForModal,
    challengeCourtsCount,
    challengeRoundsJson,
    draftPairs,
    isPoco,
    setChallengeRoundsJson,
  ]);

  const generatePlayerBasedRounds = useCallback((): {
    pairs: DraftPair[];
    rounds: any[] | null;
  } | null => {
    if (isSuper8) {
      const built = buildSuper8Schedule(super8PlayersForCurrentChallenge);
      if (!built.ok) {
        window.alert(built.error);
        return null;
      }
      setDraftPairs(built.pairs);
      built.pairs.forEach((pair) => {
        void persistPairIfNeeded(pair.cpf1, pair.cpf2);
      });
      const categoryKey = String(activeRegistrationCategoryKeyForModal || "").trim();
      const nextJson = categoryKey
        ? replaceRoundsForCategory(
            challengeRoundsJson,
            categoryKey,
            built.rounds,
          )
        : JSON.stringify(built.rounds, null, 2);
      setChallengeRoundsJson(nextJson);
      return { pairs: built.pairs, rounds: parseRoundsJson(nextJson) };
    }

    if (draftPlayers.length % 2 !== 0) {
      window.alert(siteTexts.challengeEngine.addEvenNumberAlert);
      return null;
    }
    const result = buildDrawPairs(draftPlayers);
    if (!result.ok) {
      window.alert(result.error);
      return null;
    }

    const nextPairs = result.pairs.map(([p1, p2]) => {
      const id = createPairId(p1.cpf, p2.cpf);
      const label = `${p1.name} / ${p2.name}`;
      return {
        id,
        cpf1: p1.cpf,
        cpf2: p2.cpf,
        label,
      };
    });

    setDraftPairs(nextPairs);
    nextPairs.forEach((pair) => {
      void persistPairIfNeeded(pair.cpf1, pair.cpf2);
    });

    if (result.sideConflict) {
      window.alert(siteTexts.challengeEngine.unresolvedSideAlert);
    }
    return { pairs: nextPairs, rounds: null };
  }, [
    draftPlayers,
    activeRegistrationCategoryKeyForModal,
    challengeRoundsJson,
    isSuper8,
    persistPairIfNeeded,
    setChallengeRoundsJson,
    setDraftPairs,
    super8PlayersForCurrentChallenge,
  ]);

  return {
    addDrawPlayer,
    addFixedPair,
    generateFixedPairRounds,
    generatePlayerBasedRounds,
  };
}
